import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTH_LABELS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function pad2(n) {
  return String(n).padStart(2, "0");
}

// Local calendar-day formatting (not toISOString, which is UTC and can
// silently shift the displayed day by one depending on the viewer's
// timezone) — these values represent a calendar date, not an instant.
function toISODate(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function parseISODate(str) {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function startOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isSameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatDisplayDate(date, includeYear) {
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: includeYear ? "numeric" : undefined });
}

function buildPresets(today) {
  const iso = toISODate;
  const daysAgo = (n) => {
    const d = new Date(today);
    d.setDate(d.getDate() - n);
    return d;
  };
  const startOfMonth = (monthsBack) => new Date(today.getFullYear(), today.getMonth() - monthsBack, 1);
  const endOfMonth = (monthsBack) => new Date(today.getFullYear(), today.getMonth() - monthsBack + 1, 0);

  return [
    { key: "today", label: "Today", start: iso(today), end: iso(today) },
    { key: "yesterday", label: "Yesterday", start: iso(daysAgo(1)), end: iso(daysAgo(1)) },
    { key: "last7", label: "Last 7 days", start: iso(daysAgo(6)), end: iso(today) },
    { key: "last30", label: "Last 30 days", start: iso(daysAgo(29)), end: iso(today) },
    { key: "thisMonth", label: "This month", start: iso(startOfMonth(0)), end: iso(today) },
    { key: "lastMonth", label: "Last month", start: iso(startOfMonth(1)), end: iso(endOfMonth(1)) },
  ];
}

/**
 * Self-contained date-range dropdown: preset shortcuts + a click-to-select
 * range calendar with month/year navigation. Not a MUI date picker — the
 * app already renders raw `<input type="date">` fields elsewhere, but this
 * page wants a real calendar with range preview and future-date guarding,
 * so it's built as plain SVG/DOM instead of pulling in a second styling
 * system for one widget.
 *
 * value/onChange use the same {start,end} "YYYY-MM-DD" shape the calling-
 * server stats endpoints already accept as query params.
 */
export default function CallsDateRangePicker({ value, onChange }) {
  const theme = useTheme();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => parseISODate(value.start));
  const [draftStart, setDraftStart] = useState(() => parseISODate(value.start));
  const [draftEnd, setDraftEnd] = useState(() => parseISODate(value.end));
  const [hoverDate, setHoverDate] = useState(null);
  const [pickingStart, setPickingStart] = useState(true); // which end of the range the next click sets
  const [error, setError] = useState(null);

  const today = useMemo(() => startOfDay(new Date()), []);
  const presets = useMemo(() => buildPresets(today), [today]);

  // Reset draft state to the committed value whenever the dropdown opens,
  // so a cancelled edit never leaks a half-made selection back in later.
  useEffect(() => {
    if (!open) return;
    setDraftStart(parseISODate(value.start));
    setDraftEnd(parseISODate(value.end));
    setViewMonth(parseISODate(value.start));
    setPickingStart(true);
    setHoverDate(null);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function handleEscape(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const activePresetKey = useMemo(
    () => presets.find((p) => p.start === value.start && p.end === value.end)?.key || null,
    [presets, value]
  );

  const label = activePresetKey
    ? presets.find((p) => p.key === activePresetKey).label
    : `${formatDisplayDate(parseISODate(value.start), true)} – ${formatDisplayDate(parseISODate(value.end), true)}`;

  function applyRange(start, end) {
    if (start > end) [start, end] = [end, start];
    if (start > today) {
      setError("That's in the future — pick a date up to today.");
      return;
    }
    setError(null);
    onChange({ start: toISODate(start), end: toISODate(end) });
    setOpen(false);
  }

  function handlePresetClick(preset) {
    onChange({ start: preset.start, end: preset.end });
    setOpen(false);
  }

  function handleDayClick(date) {
    if (date > today) return; // future days are disabled, not just visually — belt and suspenders
    if (pickingStart) {
      setDraftStart(date);
      setDraftEnd(date);
      setPickingStart(false);
    } else {
      const start = date < draftStart ? date : draftStart;
      const end = date < draftStart ? draftStart : date;
      setDraftStart(start);
      setDraftEnd(end);
      setPickingStart(true);
      applyRange(start, end);
    }
  }

  const gridDays = useMemo(() => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const leading = first.getDay();
    const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < leading; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), d));
    return cells;
  }, [viewMonth]);

  const rangeStart = pickingStart ? draftStart : draftStart;
  const rangeEnd = !pickingStart && hoverDate ? (hoverDate < draftStart ? draftStart : hoverDate) : draftEnd;
  const rangeStartActual = !pickingStart && hoverDate && hoverDate < draftStart ? hoverDate : rangeStart;

  return (
    <Box ref={rootRef} sx={{ position: "relative" }}>
      <Box
        component="button"
        onClick={() => setOpen((o) => !o)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          height: 40,
          px: 1.75,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          color: "text.primary",
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: 13.5,
          fontWeight: 600,
          "&:hover": { borderColor: theme.palette.text.secondary },
        }}
      >
        <CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />
        {label}
      </Box>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 20 }}
          >
            <Box
              sx={{
                display: "flex",
                borderRadius: 2.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper,
                boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
                overflow: "hidden",
              }}
            >
              {/* Presets */}
              <Box sx={{ width: 150, borderRight: `1px solid ${theme.palette.divider}`, py: 1 }}>
                {presets.map((p) => (
                  <Box
                    key={p.key}
                    onClick={() => handlePresetClick(p)}
                    sx={{
                      px: 2,
                      py: 1,
                      fontSize: 13,
                      fontWeight: activePresetKey === p.key ? 700 : 500,
                      color: activePresetKey === p.key ? "primary.main" : "text.primary",
                      cursor: "pointer",
                      "&:hover": { bgcolor: theme.palette.action.hover },
                    }}
                  >
                    {p.label}
                  </Box>
                ))}
              </Box>

              {/* Calendar */}
              <Box sx={{ p: 2, width: 300 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                  <Box
                    component="button"
                    onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                    sx={{ display: "flex", border: "none", bgcolor: "transparent", cursor: "pointer", color: "text.secondary", "&:hover": { color: "text.primary" } }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>
                    {MONTH_LABELS[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                  </Typography>
                  <Box
                    component="button"
                    onClick={() => setViewMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                    sx={{ display: "flex", border: "none", bgcolor: "transparent", cursor: "pointer", color: "text.secondary", "&:hover": { color: "text.primary" } }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </Box>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.25, mb: 0.5 }}>
                  {WEEKDAY_LABELS.map((w, i) => (
                    <Typography key={i} sx={{ fontSize: 11, fontWeight: 700, color: "text.secondary", textAlign: "center" }}>
                      {w}
                    </Typography>
                  ))}
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.25 }}>
                  {gridDays.map((date, i) => {
                    if (!date) return <Box key={i} />;
                    const disabled = date > today;
                    const isStart = isSameDay(date, rangeStartActual);
                    const isEnd = isSameDay(date, rangeEnd);
                    const inRange = date > rangeStartActual && date < rangeEnd;
                    const isToday = isSameDay(date, today);

                    return (
                      <Box
                        key={i}
                        onClick={() => !disabled && handleDayClick(date)}
                        onMouseEnter={() => !disabled && setHoverDate(date)}
                        sx={{
                          position: "relative",
                          height: 30,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 12.5,
                          fontWeight: isStart || isEnd ? 700 : 500,
                          borderRadius: isStart || isEnd ? "50%" : inRange ? 0 : 1,
                          bgcolor: isStart || isEnd ? "primary.main" : inRange ? theme.palette.action.hover : "transparent",
                          color: disabled ? theme.palette.text.disabled : isStart || isEnd ? "#fff" : "text.primary",
                          cursor: disabled ? "not-allowed" : "pointer",
                          opacity: disabled ? 0.4 : 1,
                          outline: isToday && !isStart && !isEnd ? `1px solid ${theme.palette.text.secondary}` : "none",
                          "&:hover": disabled ? {} : { bgcolor: isStart || isEnd ? "primary.main" : theme.palette.action.selected },
                        }}
                      >
                        {date.getDate()}
                      </Box>
                    );
                  })}
                </Box>

                <Typography sx={{ fontSize: 11.5, color: "text.secondary", mt: 1.5 }}>
                  {pickingStart ? "Pick a start date" : "Pick an end date"}
                </Typography>
                {error && (
                  <Typography sx={{ fontSize: 11.5, color: "#d03b3b", mt: 0.5, fontWeight: 600 }}>{error}</Typography>
                )}
              </Box>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
